/**
 * Padrão de filtros das páginas (decisão de 2026-10-02: filtros no topo).
 *
 * - `BarraFiltros`: cartão logo abaixo do título, com os grupos lado a lado (separados
 *   por uma linha vertical; quebram de linha em telas estreitas) e o indicador de
 *   carregamento sempre no canto direito.
 * - `GrupoFiltros`: todo campo fica num grupo com título curto (ex.: "Período
 *   customizado" com "Início" e "Fim").
 * - Rótulos curtos, só a primeira letra maiúscula, sem dois-pontos.
 * - Larguras fixas por tipo de campo (`LARGURA`), para as páginas ficarem iguais.
 */
import {
  Box,
  Divider,
  Group,
  Input,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { Children, Fragment, useState, type ChangeEvent, type ReactNode } from 'react';

export const LARGURA = {
  data: 160,
  numero: 130,
  selecao: 240,
  texto: 200,
} as const;

export function BarraFiltros({
  carregando = false,
  children,
}: {
  carregando?: boolean;
  children: ReactNode;
}) {
  const grupos = Children.toArray(children).filter(Boolean);
  return (
    <Paper withBorder radius="md" p="sm">
      <Group align="stretch" gap="lg" wrap="wrap">
        {grupos.map((grupo, i) => (
          <Fragment key={i}>
            {/* Em telas estreitas os grupos empilham: sem divisor. */}
            {i > 0 && <Divider orientation="vertical" visibleFrom="sm" />}
            {grupo}
          </Fragment>
        ))}
        {/* Espaço fixo à direita: o indicador aparece sem empurrar os campos. */}
        <Box ml="auto" w={24} style={{ alignSelf: 'center' }} aria-live="polite">
          {carregando && <Loader size="sm" aria-label="Carregando" />}
        </Box>
      </Group>
    </Paper>
  );
}

export function GrupoFiltros({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Stack gap={6}>
      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        {titulo}
      </Text>
      {/* Topo: campos de alturas diferentes (ex.: seleção múltipla) ficam com os rótulos alinhados. */}
      <Group gap="sm" align="flex-start" wrap="wrap">
        {children}
      </Group>
    </Stack>
  );
}

/** Teclas do ano/mês/dia. O calendário e um colar não trazem esses `inputType`. */
const DIGITACAO = new Set([
  'insertText',
  'insertCompositionText',
  'deleteContentBackward',
  'deleteContentForward',
  'deleteByCut',
]);

function emDigitacao(evento: ChangeEvent<HTMLInputElement>) {
  const nativo = evento.nativeEvent;
  return nativo instanceof InputEvent && DIGITACAO.has(nativo.inputType);
}

function dataAceita(data: string, min?: string, max?: string) {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
  const anoTexto = partes?.[1];
  const mesTexto = partes?.[2];
  const diaTexto = partes?.[3];
  if (anoTexto === undefined || mesTexto === undefined || diaTexto === undefined) return false;
  const ano = Number(anoTexto);
  const mes = Number(mesTexto);
  const dia = Number(diaTexto);
  const concreta = new Date(ano, mes - 1, dia);
  if (
    concreta.getFullYear() !== ano ||
    concreta.getMonth() !== mes - 1 ||
    concreta.getDate() !== dia
  ) {
    return false;
  }
  if (min && data < min) return false;
  if (max && data > max) return false;
  return true;
}

/**
 * Data (AAAA-MM-DD na URL).
 *
 * O campo nativo edita o ano dígito a dígito. Enquanto isso o valor fica incompleto;
 * gravar a URL a cada tecla redesenha a página e o ano volta atrás. A digitação só
 * vale ao sair do campo ou no Enter. O calendário (e um colar) valem na hora.
 * Durante a edição os limites saem do input: um ano intermediário fora de min/max
 * seria recusado pelo navegador antes dos quatro dígitos.
 */
export function CampoData({
  rotulo,
  valor,
  aoMudar,
  min,
  max,
  desabilitado = false,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (data: string) => void;
  min?: string;
  max?: string;
  desabilitado?: boolean;
}) {
  const [editando, setEditando] = useState(false);

  function confirmar(data: string, el?: HTMLInputElement) {
    if (data === valor) return;
    if (data && dataAceita(data, min, max)) {
      aoMudar(data);
      return;
    }
    if (el) el.value = valor;
  }

  return (
    <TextInput
      key={valor}
      label={rotulo}
      type="date"
      disabled={desabilitado}
      min={editando ? undefined : min}
      max={editando ? undefined : max}
      defaultValue={valor}
      onFocus={() => setEditando(true)}
      onChange={(e) => {
        const data = e.currentTarget.value;
        if (data && !emDigitacao(e)) confirmar(data, e.currentTarget);
      }}
      onBlur={(e) => {
        setEditando(false);
        confirmar(e.currentTarget.value, e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') confirmar(e.currentTarget.value, e.currentTarget);
      }}
      w={LARGURA.data}
    />
  );
}

/** Escolha entre poucas opções (SegmentedControl) com rótulo, como os demais campos. */
export function CampoOpcoes({
  rotulo,
  valor,
  aoMudar,
  opcoes,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  opcoes: { valor: string; rotulo: string }[];
}) {
  return (
    <Input.Wrapper label={rotulo}>
      <div>
        <SegmentedControl
          value={valor}
          onChange={aoMudar}
          data={opcoes.map((o) => ({ value: o.valor, label: o.rotulo }))}
        />
      </div>
    </Input.Wrapper>
  );
}

export const PERSONALIZADO = 'personalizado';

/**
 * Seletor de período do catálogo comum (backend/apps/core/periodos.py) com a opção
 * "Personalizado", que mostra o campo de data.
 *
 * Na URL: `periodo` (chave do catálogo) e `inicio` (só no Personalizado). Com `inicio`
 * preenchido a tela está em Personalizado, inclusive em links antigos que só tinham a
 * data. Ao escolher Personalizado, o campo começa na data efetiva do período atual
 * (`inicioEfetivo`, devolvida pela API); por isso a opção espera a primeira resposta.
 */
export function CampoPeriodo({
  periodo,
  inicio,
  inicioEfetivo,
  opcoes,
  aoMudar,
  rotuloData = 'Data inicial',
  min,
  max,
  desabilitado = false,
}: {
  periodo: string;
  inicio: string;
  inicioEfetivo?: string;
  opcoes: { valor: string; rotulo: string }[];
  aoMudar: (mudanca: { periodo?: string; inicio: string }) => void;
  rotuloData?: string;
  min?: string;
  max?: string;
  desabilitado?: boolean;
}) {
  const personalizado = Boolean(inicio);
  return (
    <>
      <Input.Wrapper label="Período">
        <div>
          <SegmentedControl
            value={personalizado ? PERSONALIZADO : periodo}
            disabled={desabilitado}
            onChange={(valor) =>
              valor === PERSONALIZADO
                ? aoMudar({ inicio: inicioEfetivo ?? '' })
                : aoMudar({ periodo: valor, inicio: '' })
            }
            data={[
              ...opcoes.map((o) => ({ value: o.valor, label: o.rotulo })),
              { value: PERSONALIZADO, label: 'Personalizado', disabled: !inicioEfetivo },
            ]}
          />
        </div>
      </Input.Wrapper>
      {personalizado && (
        <CampoData
          rotulo={rotuloData}
          valor={inicio}
          min={min}
          max={max}
          desabilitado={desabilitado}
          aoMudar={(data) => aoMudar({ inicio: data })}
        />
      )}
    </>
  );
}

/** Valor calculado, só para leitura (ex.: idade atual), com a aparência de um campo. */
export function CampoLeitura({
  rotulo,
  valor,
  largura = LARGURA.numero,
}: {
  rotulo: string;
  valor: string;
  largura?: number;
}) {
  return <TextInput label={rotulo} value={valor} readOnly variant="filled" w={largura} />;
}
